import { getExecutingEnvironment } from "@app/resolveModule/env";
import FetchService from "@core-ui/ApiServices/FetchService";
import Method from "@core-ui/ApiServices/Types/Method";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import type Url from "@core-ui/ApiServices/Types/Url";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { useApi, useDeferApi } from "@core-ui/hooks/useApi";
import type { AgentEvent } from "@ext/agent/core/events";
import type {
	AgentLlmEndpoint,
	AgentLlmImageRecognitionModel,
	AgentLlmModelSelection,
	AiAgentModel,
} from "@ext/agent/llm";
import { getEnterpriseSourceData } from "@ext/enterpriseCommon/logic/storage/getEnterpriseSourceData";
import t from "@ext/localization/locale/translate";
import { useWorkspaceAi } from "@ext/workspace/components/useWorkspaceAi";
import { useCallback } from "react";
import { useAgentChatHealthcheck } from "../store/AgentChatHealthcheckStore";
import {
	getSessions,
	markSessionModelUsed,
	setSessionBrowserAllowed,
	useActiveSessionBrowserAllowed,
} from "../store/AgentStore";
import { setQuote, useChatQuote } from "../store/ChatStore";
import type { SessionStatePayload } from "../types/chat";
import { snapshotAgentSession } from "../utils/agentSessionActivity";
import { useAgentPollTick } from "./useAgentPollTick";
import { useDraftPersistence } from "./useDraftPersistence";
import { type SelectedAgentModel, useSelectedAgentModel } from "./useSelectedAgentModel";

type MessageSendPayload = {
	sessionId?: string;
	error?: string;
	message?: string;
};

const CANCEL_OPTS = {
	method: Method.POST,
	mime: MimeTypes.json,
	consumeError: true,
} as const;

const toImageRecognitionModel = (model: AiAgentModel): AgentLlmImageRecognitionModel => ({
	model: model.id,
	modelType: model.type,
	modelName: model.name,
	protocol: model.protocol,
});

const toModelSelection = ({ model, reasoningEffort }: SelectedAgentModel): AgentLlmModelSelection => ({
	model: model.id,
	modelType: model.type,
	modelName: model.name,
	protocol: model.protocol,
	reasoningEffort,
});

const errorText = (data: MessageSendPayload | null, fallback: string): string => {
	switch (data?.error) {
		case "session_not_found":
			return t("agent.chat-error.session-not-found");
		case "session_cancelled":
			return t("agent.chat-error.session-cancelled");
		case "empty_message":
			return t("agent.chat-error.empty-message");
		case "agent_failed":
			return t("agent.chat-error.agent-failed");
		default:
			return fallback;
	}
};

const requestMessageSend = async (url: Url, body: object) => {
	const res = await FetchService.fetch<MessageSendPayload>(
		url,
		JSON.stringify(body),
		MimeTypes.json,
		Method.POST,
		false,
	);
	return { ok: res.ok, data: (await res.json().catch(() => null)) as MessageSendPayload | null };
};

const getAgentChatUrl = (baseUrl: string | undefined) => {
	if (!baseUrl) return undefined;
	const trimmed = baseUrl.replace(/\/+$/, "");
	return `${trimmed}/ai-agent/chat`;
};

const getAgentAudioUrl = (baseUrl: string | undefined) => {
	if (!baseUrl) return undefined;
	const trimmed = baseUrl.replace(/\/+$/, "");
	return `${trimmed}/ai-agent/audio`;
};

const toManagedEndpoint = ({
	isPortal,
	gesUrl,
	gesToken,
	gesCloudUrl,
	modelSelection,
}: {
	isPortal: boolean;
	gesUrl?: string;
	gesToken: string;
	gesCloudUrl?: string;
	modelSelection: AgentLlmModelSelection & { imageRecognitionModel?: AgentLlmImageRecognitionModel };
}): AgentLlmEndpoint => {
	if (isPortal) return { kind: "enterprise", url: "", audioUrl: "", token: "", ...modelSelection };
	if (gesUrl)
		return {
			kind: "enterprise",
			url: getAgentChatUrl(gesUrl) ?? "",
			audioUrl: getAgentAudioUrl(gesUrl) ?? "",
			token: gesToken,
			...modelSelection,
		};
	return {
		kind: "enterpriseCloud",
		url: getAgentChatUrl(gesCloudUrl) ?? "",
		audioUrl: getAgentAudioUrl(gesCloudUrl) ?? "",
		...modelSelection,
	};
};

const getDirectChatCompletionsUrl = (baseUrl: string | undefined) => {
	if (!baseUrl) return undefined;
	const trimmed = baseUrl.replace(/\/+$/, "");
	return `${trimmed}/api/v1/chat/completions`;
};

type Args = {
	sessionId: string | null;
	sessionLoading: boolean;
	openCatalogName: string | null;
	openItemPath: string | null;
	fetchSessionState: () => Promise<SessionStatePayload | null>;
	applySessionState: (data: SessionStatePayload | null) => AgentEvent[];
	startPolling: (tick: () => Promise<void> | void) => void;
	stopPolling: () => void;
	appendError: (message: string) => void;
};

export const useAgentSender = ({
	sessionId,
	sessionLoading,
	openCatalogName,
	openItemPath,
	fetchSessionState,
	applySessionState,
	startPolling,
	stopPolling,
	appendError,
}: Args) => {
	const workspacePath = WorkspaceService.current()?.path ?? "";
	const sourceDatas = SourceDataService.value;
	const { getData: getWorkspaceAiData } = useWorkspaceAi(workspacePath);
	const apiUrlCreator = ApiUrlCreatorService.value;

	const browserAllowed = useActiveSessionBrowserAllowed();
	const { call: callSetBrowserAllowed } = useDeferApi<unknown>({
		url: (api) => api.getAgentBrowserSetAllowedUrl(),
		opts: { method: Method.POST, mime: MimeTypes.json, consumeError: true },
	});

	const setBrowserAllowed = useCallback(
		(allowed: boolean) => {
			if (!sessionId) return;
			setSessionBrowserAllowed(sessionId, allowed);
			void callSetBrowserAllowed({ opts: { body: JSON.stringify({ sessionId, allowed }) } });
		},
		[callSetBrowserAllowed, sessionId],
	);

	const {
		sending,
		setSending,
		sendingRef,
		showAgentThinking,
		setShowAgentThinkingIfChanged,
		sessionSnapshotRef,
		pollTickRef,
	} = useAgentPollTick({
		sessionId,
		sessionLoading,
		openCatalogName,
		openItemPath,
		fetchSessionState,
		flushAndRefresh: applySessionState,
		startPolling,
		stopPolling,
	});

	const {
		draft,
		setDraft,
		selectedSkillName,
		setSelectedSkillName,
		draftAttachments: attachments,
		setDraftAttachments: setAttachments,
		hydrating,
		flushDraft,
		persistDraft,
		clearOnSend,
		restoreBaseline,
	} = useDraftPersistence(sessionId, sendingRef);

	const quote = useChatQuote();

	const { call: callCancel } = useApi<void>({
		url: (api) => api.getAgentSessionCancelUrl(sessionId ?? ""),
		opts: CANCEL_OPTS,
	});

	const { gesUrl } = PageDataContextService.value.conf.enterprise;
	const { url: gesCloudUrl, enabled: gesCloudEnabled } = PageDataContextService.value.conf.enterpriseCloud;
	const selection = useSelectedAgentModel();
	const { imageRecognitionModel } = useAgentChatHealthcheck();

	const send = useCallback(async () => {
		const text = draft.trim();
		if (!text || sending) return;

		const pickedAttachments = attachments;
		const pickedSkill = selectedSkillName;
		const pickedQuote = quote;
		const restoreDraft = () => {
			setDraft(text);
			setAttachments(pickedAttachments);
			setQuote(pickedQuote);
			if (sessionId) {
				persistDraft(sessionId, text, pickedSkill, pickedAttachments, pickedQuote);
				restoreBaseline(text, pickedSkill, pickedAttachments, pickedQuote);
			}
		};
		setSending(true);
		sendingRef.current = true;
		sessionSnapshotRef.current = snapshotAgentSession(null);
		setShowAgentThinkingIfChanged(true);
		flushDraft();
		setDraft("");
		setAttachments([]);
		setQuote(null);

		try {
			const activeId = sessionId;
			if (!activeId) {
				restoreDraft();
				appendError(t("agent.chat-error.no-active-session"));
				return;
			}

			clearOnSend(activeId, pickedSkill);

			let endpoint: AgentLlmEndpoint;
			if (getExecutingEnvironment() === "next" || gesUrl || (gesCloudUrl && gesCloudEnabled)) {
				if (!selection) {
					restoreDraft();
					appendError(t("agent.chat-error.no-model-selected"));
					return;
				}
				const modelSelection = toModelSelection(selection);
				endpoint = toManagedEndpoint({
					isPortal: getExecutingEnvironment() === "next",
					gesUrl,
					gesToken: getEnterpriseSourceData(sourceDatas ?? [], gesUrl)?.token ?? "",
					gesCloudUrl,
					modelSelection: {
						...modelSelection,
						imageRecognitionModel: imageRecognitionModel && toImageRecognitionModel(imageRecognitionModel),
					},
				});
				markSessionModelUsed(activeId, {
					type: modelSelection.modelType,
					name: modelSelection.modelName,
					reasoningEffort: modelSelection.reasoningEffort,
				});
			} else {
				const workspaceAiData = await getWorkspaceAiData().catch(() => undefined);
				const workspaceUrl = getDirectChatCompletionsUrl(workspaceAiData?.aiApiUrl);
				if (!workspaceUrl) {
					restoreDraft();
					appendError(t("agent.error-type.unauthorized"));
					return;
				}

				endpoint = {
					kind: "direct",
					url: workspaceUrl,
					apiKey: workspaceAiData?.aiToken ?? "",
					protocol: "openai-completions",
				};
			}

			startPolling(() => pollTickRef.current().then(() => undefined));

			// DeferApi does not support parallel requests
			const url = apiUrlCreator.getAgentMessageSendUrl();
			const body = {
				sessionId: activeId,
				text,
				endpoint,
				attachments: pickedAttachments,
				quote: pickedQuote ?? undefined,
				openCatalogName,
				openItemPath,
				useSkill: pickedSkill,
			};
			let { ok, data } = await requestMessageSend(url, body);
			if (getExecutingEnvironment() === "next" && data?.error === "session_not_found") {
				const clientSession = getSessions().find((s) => s.id === activeId);
				({ ok, data } = await requestMessageSend(url, { ...body, clientSession }));
			}

			if (!ok) {
				restoreDraft();
				appendError(errorText(data, t("agent.chat-error.send-message-error")));
				return;
			}

			if (!data) {
				restoreDraft();
				appendError(t("agent.chat-error.send-message-error"));
				return;
			}

			if (data.error) {
				restoreDraft();
				appendError(errorText(data, t("agent.chat-error.get-response-error")));
				return;
			}
		} catch {
			restoreDraft();
			appendError(t("agent.chat-error.agent-failed"));
		} finally {
			sendingRef.current = false;
			await pollTickRef.current();
		}
	}, [
		appendError,
		attachments,
		clearOnSend,
		draft,
		gesCloudUrl,
		gesCloudEnabled,
		flushDraft,
		gesUrl,
		imageRecognitionModel,
		selection,
		openCatalogName,
		openItemPath,
		persistDraft,
		pollTickRef,
		quote,
		restoreBaseline,
		selectedSkillName,
		sending,
		sendingRef,
		sessionId,
		sessionSnapshotRef,
		setAttachments,
		setDraft,
		setSending,
		startPolling,
		setShowAgentThinkingIfChanged,
		getWorkspaceAiData,
	]);

	const cancel = useCallback(async () => {
		if (!sessionId) return;
		sendingRef.current = false;
		setShowAgentThinkingIfChanged(false);
		sessionSnapshotRef.current = snapshotAgentSession(null);
		stopPolling();
		await callCancel();
		await pollTickRef.current();
	}, [
		callCancel,
		pollTickRef,
		sendingRef,
		sessionId,
		sessionSnapshotRef,
		stopPolling,
		setShowAgentThinkingIfChanged,
	]);

	return {
		draft,
		setDraft,
		attachments,
		setAttachments,
		selectedSkillName,
		setSelectedSkillName,
		browserAllowed,
		setBrowserAllowed,
		send,
		cancel,
		sending,
		hydrating,
		showAgentThinking,
	};
};
